import { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { T, UI, FONT } from "../constants/tokens";
import { TYPE } from "../constants/typography";
import { useTheme, cssVar } from "../utils/theme";
import { useIsMobile } from "../utils/hooks";
import { fmtPct, MINUS } from "../utils/formatters";
import { RANGES, fetchPriceHistory, rangeChange, axisLabel, pointLabel, spanLabel, fmtVolume } from "../utils/priceHistory";
import LTR from "./LTR";
import Icon from "./Icon";
import { Skeleton } from "./Skeleton";
import TradingViewChart from "./TradingViewChart";

// ─── The price chart on the company page (26/10/2026, mockup 2) ──────────────
// TradingView Lightweight Charts™ over Yahoo Finance bars (utils/
// priceHistory.js). A line by default · the price, in ink, since a price is
// not a movement · and candles on request, in gain and loss, since each one
// is that day's move. Small volume bars at the foot. A finger or the mouse
// on the chart draws a vertical line with that point's date and price.
// "גרף מלא" opens TradingView's own full chart (TradingViewChart).
//
// The library is Apache 2.0 and asks for attribution: its NOTICE line under
// the chart, a link to tradingview.com, and its logo on the chart
// (attributionLogo, on by default). It is loaded only when a chart is drawn.
let libPromise = null;
const loadLib = () => (libPromise ||= import("lightweight-charts"));

// The year as in the bundled version's own header (5.2.1).
const NOTICE = "TradingView Lightweight Charts™ · Copyright (c) 2026 TradingView, Inc.";

const CUR_SIGN = { USD: "$", ILS: "₪", EUR: "€", GBP: "£" };
// Tel Aviv quotes come in agorot (ILA): shown in shekels.
const toShown = (v, cur) => (cur === "ILA" ? v / 100 : v);
const shownCur = cur => (cur === "ILA" ? "ILS" : cur);
const money = (v, cur) => (!cur ? v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : `${CUR_SIGN[cur] ?? ""}${v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${CUR_SIGN[cur] ? "" : ` ${cur}`}`);

// "#58627A" → "rgba(88,98,122,0.3)" · a bar a step quieter than the text.
function withAlpha(color, a) {
  const m = /^#([0-9a-f]{6})$/i.exec(color || "");
  if (!m) return color;
  const n = parseInt(m[1], 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
}

// One bar per time, oldest first · the library refuses anything else.
function cleanBars(bars) {
  const byTime = new Map();
  for (const b of bars || []) byTime.set(b.t, b);
  return [...byTime.values()].sort((x, y) => (typeof x.t === "number" ? x.t - y.t : String(x.t).localeCompare(String(y.t))));
}

function Segmented({ items, value, onChange, dir, label, fill }) {
  return (
    <div role="group" aria-label={label} dir={dir}
      style={{ display: "flex", gap: 2, background: UI.fill.subtle, borderRadius: 8, padding: 2, flex: fill ? 1 : "none" }}>
      {items.map(it => {
        const on = it.id === value;
        return (
          <button key={it.id} onClick={() => onChange(it.id)} aria-pressed={on}
            style={{
              flex: fill ? 1 : "none", minHeight: 36, padding: "6px 10px", borderRadius: 6, border: "none", cursor: "pointer",
              background: on ? UI.state.surface : "transparent", color: on ? UI.state.fg : UI.text.secondary,
              fontSize: 12.5, fontWeight: 700, fontFamily: /[A-Z0-9]/.test(it.label) ? FONT.latin : "inherit",
            }}>
            {it.label}
          </button>
        );
      })}
    </div>
  );
}

// `symbol` is Yahoo's. An index (`points`) is in points, so no currency sign;
// `tvSymbol` names it for TradingView's full chart when the two differ.
export default function PriceChart({ symbol, tvSymbol = symbol, label = symbol, points = false, delay = 15 }) {
  const isMobile = useIsMobile();
  const theme = useTheme();
  const [range, setRange] = useState("1mo");
  const [kind, setKind] = useState("line");
  const [history, setHistory] = useState(undefined);   // undefined = loading · null = failed
  const [attempt, setAttempt] = useState(0);
  const [point, setPoint] = useState(null);
  const [full, setFull] = useState(false);
  const boxRef = useRef(null);
  const chartRef = useRef(null);
  const height = isMobile ? 240 : 300;

  useEffect(() => {
    let alive = true;
    setHistory(undefined);
    setPoint(null);
    fetchPriceHistory(symbol, range).then(h => { if (alive) setHistory(h); });
    return () => { alive = false; };
  }, [symbol, range, attempt]);

  // The chart itself: rebuilt when the bars, the kind or the theme change.
  // The canvas cannot read CSS variables, so the theme's colours are resolved.
  useEffect(() => {
    const el = boxRef.current;
    if (!el || !history) return undefined;
    let chart = null;
    let disposed = false;
    loadLib().then(L => {
      if (disposed) return;
      const ink = cssVar("--si-text-primary"), muted = cssVar("--si-text-muted");
      const grid = cssVar("--si-line-hairline");
      const up = cssVar("--si-gain"), down = cssVar("--si-loss");
      const bars = cleanBars(history.bars);
      const r = history.range;
      chart = L.createChart(el, {
        autoSize: true,
        layout: { background: { type: "solid", color: "transparent" }, textColor: muted, fontFamily: `"Archivo", system-ui, sans-serif`, fontSize: 11, attributionLogo: true },
        grid: { vertLines: { visible: false }, horzLines: { color: grid } },
        // The price scale on the left: the page reads right to left.
        leftPriceScale: { visible: true, borderVisible: false, scaleMargins: { top: 0.2, bottom: 0.24 } },
        rightPriceScale: { visible: false },
        timeScale: { borderVisible: false, fixLeftEdge: true, fixRightEdge: true, lockVisibleTimeRangeOnResize: true,
          tickMarkFormatter: t => axisLabel(r, t) },
        crosshair: { mode: L.CrosshairMode.Magnet,
          vertLine: { color: muted, width: 1, style: L.LineStyle.Solid, labelVisible: false },
          horzLine: { visible: false, labelVisible: false } },
        // A still picture of the range: no dragging or zooming, so a finger
        // on it moves the line and a swipe up still scrolls the page.
        handleScroll: false, handleScale: false,
        localization: { locale: "he-IL", // Whole numbers on the axis of a price over 100.
          priceFormatter: p0 => { const p = points ? p0 : toShown(p0, history.currency); return p.toLocaleString("en-US", { minimumFractionDigits: p >= 100 ? 0 : 2, maximumFractionDigits: p >= 100 ? 0 : 2 }); },
          timeFormatter: t => pointLabel(r, t) },
      });
      const series = kind === "candle"
        ? chart.addSeries(L.CandlestickSeries, { priceScaleId: "left", upColor: up, downColor: down, borderVisible: false, wickUpColor: up, wickDownColor: down, priceLineVisible: false, lastValueVisible: false })
        : chart.addSeries(L.LineSeries, { priceScaleId: "left", color: ink, lineWidth: 2, priceLineVisible: false, lastValueVisible: false, crosshairMarkerRadius: 4, crosshairMarkerBorderColor: ink, crosshairMarkerBackgroundColor: ink });
      series.setData(kind === "candle"
        ? bars.map(b => ({ time: b.t, open: b.o, high: b.h, low: b.l, close: b.c }))
        : bars.map(b => ({ time: b.t, value: b.c })));
      // 1D: yesterday's close, the line the day's change is measured from.
      if (r === "1d" && history.prevClose > 0) {
        series.createPriceLine({ price: history.prevClose, color: muted, lineWidth: 1, lineStyle: L.LineStyle.Dashed, axisLabelVisible: false });
      }
      // Volume: small bars at the foot, one quiet colour (how much traded,
      // not which way).
      if (bars.some(b => b.v > 0)) {
        const vol = chart.addSeries(L.HistogramSeries, { priceScaleId: "vol", color: withAlpha(muted, 0.2), priceLineVisible: false, lastValueVisible: false, priceFormat: { type: "volume" } });
        chart.priceScale("vol").applyOptions({ scaleMargins: { top: 0.84, bottom: 0 }, visible: false });
        vol.setData(bars.map(b => ({ time: b.t, value: b.v > 0 ? b.v : 0 })));
      }
      chart.timeScale().fitContent();
      // The canvas draws text in its element's direction: right to left, so
      // "1 באוק׳" is not drawn as "באוק׳ 1". The layout stays as it is.
      el.querySelectorAll("canvas").forEach(c => { c.style.direction = "rtl"; });

      const byKey = new Map(bars.map((b, i) => [typeof b.t === "number" ? b.t : String(b.t), i]));
      const keyOf = t => (typeof t === "number" ? t : typeof t === "string" ? t
        : `${t.year}-${String(t.month).padStart(2, "0")}-${String(t.day).padStart(2, "0")}`);
      // The library measures x from the plot area, which starts after the
      // price scale on the left · the label is placed in the whole box.
      const scaleW = () => chart.priceScale("left").width() || 0;
      const show = (i, x) => {
        const b = bars[i];
        if (!b) { setPoint(null); return; }
        setPoint({ x: x + scaleW(), time: b.t, price: b.c, v: b.v, o: b.o, h: b.h, l: b.l });
      };
      // A finger: the line follows it while it touches, and goes when it lifts.
      let touching = false;
      chart.subscribeCrosshairMove(p => {
        if (!p || !p.time || !p.point || p.point.x < 0) { if (!touching) setPoint(null); return; }
        const i = byKey.get(keyOf(p.time));
        if (i != null) show(i, p.point.x);
      });
      const at = e => {
        const rect = el.getBoundingClientRect();
        const x = e.clientX - rect.left - scaleW();
        const logical = chart.timeScale().coordinateToLogical(x);
        if (logical == null) return;
        const i = Math.max(0, Math.min(bars.length - 1, Math.round(logical)));
        const b = bars[i];
        chart.setCrosshairPosition(b.c, b.t, series);
        show(i, chart.timeScale().timeToCoordinate(b.t) ?? x);
      };
      const down_ = e => { if (e.pointerType !== "mouse") { touching = true; at(e); } };
      const move = e => { if (touching) at(e); };
      const up_ = () => { if (touching) { touching = false; chart.clearCrosshairPosition(); setPoint(null); } };
      el.addEventListener("pointerdown", down_);
      el.addEventListener("pointermove", move);
      el.addEventListener("pointerup", up_);
      el.addEventListener("pointercancel", up_);
      chartRef.current = { chart, off: () => {
        el.removeEventListener("pointerdown", down_); el.removeEventListener("pointermove", move);
        el.removeEventListener("pointerup", up_); el.removeEventListener("pointercancel", up_);
      } };
    });
    return () => {
      disposed = true;
      if (chartRef.current) { chartRef.current.off(); chartRef.current = null; }
      if (chart) chart.remove();
      setPoint(null);
    };
  }, [history, kind, theme, points]);

  const closeFull = useCallback(() => setFull(false), []);
  const [vh, setVh] = useState(() => (typeof window === "undefined" ? 800 : window.innerHeight));
  useEffect(() => {
    if (!full) return undefined;
    const onResize = () => setVh(window.innerHeight);
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [full]);
  useEffect(() => {
    if (!full) return undefined;
    // First, in the capture phase, and marked as handled: a panel under the
    // full chart (IndexChartPanel) closes on Escape too, and must not here.
    const onKey = e => { if (e.key === "Escape") { e.preventDefault(); e.stopImmediatePropagation(); closeFull(); } };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [full, closeFull]);

  const chg = rangeChange(history);
  const cur = points ? null : (history?.currency || "USD");
  const cfg = RANGES.find(x => x.id === range);
  const tone = !chg || Math.abs(chg.pct) < 0.005 ? UI.text.primary : chg.pct > 0 ? UI.gain : UI.loss;
  const tipW = 150;
  const boxW = boxRef.current?.clientWidth || 0;

  return (
    <div>
      {/* The change over the range, above the chart */}
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap", minHeight: 22 }}>
        {chg ? (
          <>
            <span style={{ fontSize: 12.5, fontWeight: 600, color: UI.text.muted }}>{spanLabel(range, history)}</span>
            <LTR className="si-num" style={{ fontSize: 15, fontWeight: 700, color: tone }}>
              {chg.dollar > 0 ? "+" : chg.dollar < 0 ? MINUS : ""}{money(cur ? toShown(Math.abs(chg.dollar), cur) : Math.abs(chg.dollar), cur && shownCur(cur))} · {fmtPct(chg.pct)}
            </LTR>
          </>
        ) : history === undefined ? <Skeleton h={16} w={160}/> : null}
      </div>

      <div style={{ position: "relative", marginTop: 8 }}>
        {history === null ? (
          <div style={{ height, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 10, background: UI.surface.sunken, borderRadius: T.radius.md, fontSize: 13.5, color: UI.text.muted }}>
            {range === "1d" ? "אין עדיין נתונים ליום המסחר הזה · אפשר לבחור טווח אחר" : <>הגרף לא נטען · אין נתונים מ-<LTR>Yahoo Finance</LTR></>}
            <button onClick={() => setAttempt(n => n + 1)}
              style={{ background: "transparent", border: `1px solid ${UI.line.hairline}`, borderRadius: 999, padding: "4px 14px", minHeight: 36, fontSize: 13, fontWeight: 700, color: UI.interactive, cursor: "pointer", fontFamily: "inherit" }}>
              נסה שוב
            </button>
          </div>
        ) : (
          <>
            <div ref={boxRef} data-price-chart style={{ height, width: "100%", touchAction: "pan-y", direction: "ltr" }}/>
            {history === undefined && <div style={{ position: "absolute", inset: 0 }}><Skeleton h={height} w="100%"/></div>}
          </>
        )}
        {point && (
          <div role="status" style={{
            position: "absolute", top: 0, left: Math.max(0, Math.min(boxW - tipW, point.x - tipW / 2)), width: tipW,
            background: UI.surface.raised, border: `1px solid ${UI.line.hairline}`, borderRadius: 8, boxShadow: UI.shadow.card,
            padding: "6px 9px", pointerEvents: "none", zIndex: 2, textAlign: "start",
          }}>
            <div style={{ fontSize: 11.5, color: UI.text.muted }}>{pointLabel(range, point.time)}</div>
            <LTR className="si-num" style={{ fontSize: 14, fontWeight: 700, color: UI.text.primary }}>{money(cur ? toShown(point.price, cur) : point.price, cur && shownCur(cur))}</LTR>
            {fmtVolume(point.v) && <div style={{ fontSize: 11, color: UI.text.muted }}>מחזור <LTR className="si-num">{fmtVolume(point.v)}</LTR></div>}
          </div>
        )}
      </div>

      {/* Range, line or candles, and the full chart */}
      <div style={{ display: "flex", gap: 8, alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", marginTop: 12, flexDirection: isMobile ? "column" : "row" }}>
        <div style={{ display: "flex", width: isMobile ? "100%" : "auto" }}>
          <Segmented label="טווח" dir="ltr" fill={isMobile} items={RANGES.map(x => ({ id: x.id, label: x.label }))} value={range} onChange={setRange}/>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", justifyContent: "space-between", width: isMobile ? "100%" : "auto" }}>
          <Segmented label="סוג גרף" items={[{ id: "line", label: "קו" }, { id: "candle", label: "נרות" }]} value={kind} onChange={setKind}/>
          <button onClick={() => setFull(true)}
            style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "transparent", border: `1px solid ${UI.line.hairline}`, borderRadius: 999, padding: "5px 14px", minHeight: 36, fontSize: 13, fontWeight: 700, color: UI.interactive, cursor: "pointer", fontFamily: "inherit" }}>
            <Icon name="expand" size={14} color={UI.interactive}/> גרף מלא
          </button>
        </div>
      </div>

      <div style={{ ...TYPE.caption, marginTop: 12, paddingTop: 10, borderTop: `1px solid ${UI.line.hairline}` }}>
        מקור: <LTR>Yahoo Finance</LTR> · {cfg?.res} · {delay ? `בעיכוב של עד ${delay} דקות` : "כמעט בזמן אמת"}{range === "1d" && history?.prevClose > 0 ? " · הקו המקווקו: הסגירה הקודמת" : ""}
        <br/>
        גרף: <a href="https://www.tradingview.com/" target="_blank" rel="noopener noreferrer" style={{ color: UI.interactive, fontWeight: 700, textDecoration: "none" }}><LTR>TradingView</LTR></a> · <LTR>{NOTICE}</LTR>
      </div>

      {/* On document.body: the company page fades in with a transform, and
          a fixed layer inside a transformed parent is fixed to that parent. */}
      {full && createPortal(
        <div role="dialog" aria-modal="true" aria-label={`${label} · גרף מלא`} data-full-chart
          dir="rtl" style={{ position: "fixed", inset: 0, zIndex: 1600, background: UI.surface.page, display: "flex", flexDirection: "column" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 16px", borderBottom: `1px solid ${UI.line.hairline}`, background: UI.surface.raised }}>
            <span style={{ fontSize: 15, fontWeight: 800, color: UI.text.primary }}><LTR>{label}</LTR> · גרף מלא</span>
            <button onClick={closeFull} aria-label="סגור את הגרף המלא"
              style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "transparent", border: `1px solid ${UI.line.hairline}`, borderRadius: T.radius.md, minHeight: 44, padding: "0 14px", fontSize: 13, fontWeight: 700, color: UI.text.secondary, cursor: "pointer", fontFamily: "inherit" }}>
              <Icon name="x" size={14} color={UI.text.secondary}/> סגור
            </button>
          </div>
          <div style={{ flex: 1, padding: 8, minHeight: 0 }}>
            <TradingViewChart symbol={tvSymbol} height={Math.max(320, vh - 82)}/>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
